/**
 * Ob das Display wach bleibt — die Seite entscheidet, die App führt aus.
 *
 * Ticket 744 (28.07.2026, Akku): Wachgehalten wird nur, solange jemand da
 * ist. Jede Berührung, jeder Tastendruck und jedes Scrollen beginnt das
 * Fenster neu, eine laufende Wiedergabe zählt als Tätigkeit (05.09.2026).
 * Nach fünf Minuten Ruhe darf das Display schlafen; die nächste Berührung
 * holt es zurück. Zwei Wachhalter führen das aus: die der Seite (WakeLock-API,
 * NoSleep-Video) und der der App (`isIdleTimerDisabled`, PawbotRecorder).
 *
 * ⚠️ 28.09.2026 (Ticket 2754) — DIE NEUE SEITE SAGTE DER APP NICHTS.
 *
 *    Martin: „Display always on funktioniert nicht mehr … das ist neu,
 *    vorher ging es.“ Die App setzt ihren Wachhalter beim Kaltstart auf
 *    „wach“; danach ändert ihn nur die Seite — und bis heute nur, wenn sich
 *    der Zustand der Seite ÄNDERTE.
 *
 *    Eine neu geladene Seite beginnt aber immer mit „wach“ und meldete darum
 *    nichts. Stand die App davor auf Ruhe (fünf Minuten nichts), blieb sie
 *    dort: Die Seite glaubte „wach“, die App ließ das Display schlafen, und
 *    keine Berührung half, weil es aus Sicht der Seite nichts zu ändern gab.
 *    Das hielt bis zum nächsten Kaltstart der App.
 *
 *    Neu ist das seit dem stillen Nachladen (25.09.2026, pre.3289): Es lädt
 *    die Seite genau dann neu, wenn sie verdeckt wird — also gerade, nachdem
 *    das Display in der Ruhe ausgegangen ist.
 *
 *    Darum sagt die Seite der App ihren Stand beim Start und bei jeder
 *    Rückkehr nach vorne, nicht nur beim Wechsel. Die Seite bleibt der eine
 *    Besitzer; die App hält nur fest, was sie zuletzt gehört hat.
 *
 * ⚠️ 28.09.2026 (Ticket 2754, Nachbesserung) — WER NUR SPRICHT, WAR NIE DA.
 *
 *    Bis 22.09. begann jede Frage mit einem Tippen auf die Sprechtaste, und
 *    das Tippen hielt das Display wach. Seit 23.09. startet die Aufnahme auch
 *    über das Wach-Wort, seit 27.09. nach jeder Antwort von selbst; am 28.09.
 *    kamen 33 Aufnahmen per Tippen und 125 ohne. Zwei Lücken der Ruheregel
 *    wurden dadurch sichtbar:
 *
 *    - Die Rückkehr nach vorne zählte nicht. Wer das Telefon entsperrt, ist
 *      da — die Seite rechnete aber mit der letzten Berührung von vorher und
 *      konnte das Display gleich im ersten Takt ruhen lassen.
 *    - Wiedergabe verlängerte nur ein laufendes Fenster. Ruhte das Display
 *      einmal, holte keine vorgelesene Antwort es zurück, nur ein Finger.
 *
 *    Beides zählt jetzt wie eine Berührung. Die Ruheregel selbst bleibt:
 *    fünf Minuten ohne Berührung, Rückkehr und Wiedergabe → Ruhe (#744).
 */

/** Ruhezeit ohne Tätigkeit, nach der das Display schlafen darf (#744). */
export const DISPLAY_RUHE_MS = 5 * 60_000;

interface DisplayWachWege {
  /** Die Wachhalter der Seite: WakeLock-API und NoSleep-Video. */
  seite: (wach: boolean) => void;
  /** Der Wachhalter der App (`isIdleTimerDisabled`); im Browser ohne Wirkung. */
  app: (wach: boolean) => void;
  /** Uhr in ms — nur für Tests ersetzt. */
  jetzt?: () => number;
}

export interface DisplayWach {
  /** Soll das Display gerade wach bleiben? */
  readonly wach: boolean;
  /** Berührung, Taste oder Scrollen: das Fenster beginnt neu. */
  beruehrt(): void;
  /**
   * Der Takt der SICHTBAREN Seite. `klingt`: Wiedergabe zählt als Tätigkeit.
   * `nimmtAuf` (30.10.2026): eine laufende Sprechtasten-Aufnahme ebenso — wer
   * diktiert, tippt nicht, und das Display darf nicht mitten im Diktat
   * einschlafen (die Seite friert dann ein, die Aufnahme reißt ab). Kein
   * Schalter, keine Dauerhaltung: mit dem Ende der Aufnahme beginnt die Ruhe
   * von fünf Minuten wieder, jeder Takt ohne Aufnahme zählt weiter.
   */
  takt(klingt: boolean, nimmtAuf?: boolean): void;
  /** Die Seite ist wieder vorne: zählt als Tätigkeit, die App hört den Stand neu. */
  vorne(): void;
}

export function erzeugeDisplayWach(w: DisplayWachWege): DisplayWach {
  const jetzt = w.jetzt ?? (() => Date.now());
  let wach = true;
  let letzteTaetigkeit = jetzt();

  const setzen = (neu: boolean): void => {
    if (wach === neu) return;
    wach = neu;
    w.seite(neu);
    w.app(neu);
  };

  /** Jemand ist da: das Fenster beginnt neu, ein ruhendes Display wird wach. */
  const taetig = (): void => {
    letzteTaetigkeit = jetzt();
    setzen(true);
  };

  // Ticket 2754: Die App weiß nicht, dass hier eine neue Seite beginnt.
  w.app(wach);

  return {
    get wach() {
      return wach;
    },
    beruehrt: taetig,
    takt(klingt: boolean, nimmtAuf = false) {
      if (klingt || nimmtAuf) taetig();
      else if (jetzt() - letzteTaetigkeit >= DISPLAY_RUHE_MS) setzen(false);
    },
    vorne() {
      const warWach = wach;
      taetig();
      // War die Seite schon wach, hat `taetig` nichts gemeldet — die App
      // hält aber nur fest, was sie zuletzt gehört hat.
      if (warWach) w.app(true);
    },
  };
}
